<template>
  <zm-form ref="zmoneForm" label-position="top" class="opc-config-form">
    <subscription-switch @switch-plan="handleSwitchPlan" />
    <zm-form-item class="opc-zmone-single-edit__form-item">
      <div class="opc-zmone-single-edit__form-item-content-row">
        <div class="opc-zmone__form-label">
          {{ $t('billing.simplify.select_license') }}
          <zm-tooltip class="item" disallow-learn-about aria-label="Learn more about Select the number of users" placement="top">
            <template #content>
              <span class="opc-zone__tip" v-html="$t('billing.zone.attendee_info_edit')"></span>
            </template>
            <tips-icon
              type="info"
              @click="ctx.handleSelectNumOfUsersTipsClick"
            />
          </zm-tooltip>
          <bought-license :type="planTypes.ZOOM_MEETING" />
        </div>
        <div class="opc-zmone__quantity-selector-container">
          <InputNumberWithPopover class="opc-zmone__quantity-selector"
            v-model="form.selectedNumber"
            size="large"
            @change="submitForm"
            controls-position="both"
            :min="ctx.minHost"
            :max="ctx.maxHost"
            :label="`${ctx.title} Select the number of users`"
            :decrease-tips="ctx.popUpDecreaseTips"
            :increase-tips="ctx.increaseTips"
            :data-testid="`checkout-input-license-${currentSelectedMeetingPlan ? currentSelectedMeetingPlan.plan_code : ''}`"
          />
          <user-changes :type="planTypes.ZOOM_MEETING" :value="form.selectedNumber" />
        </div>
      </div>
      <zm-banner show-close class="reason-tips" v-if="ctx.licenseTips" type="info">
        <p @click='ctx.onLicenseTipsClick' v-html="ctx.licenseTips"></p>
      </zm-banner>
    </zm-form-item>
    <zm-form-item
      :class="[...ctx.classForSimplifySubscriptionCycle]"
      :label="ctx.mxIsSimplifyForProEdit ? $t('billing.simplify.subscription_cycle') : $t('billing.select_billing_cycle')">
      <template #label-suffix>
        <div v-if="ctx.mxIsSimplifyForProEdit" class="opc-plan__billing-text">
          {{ getCurrentBilledText() }}
        </div>
      </template>
      <zm-radio-group
        v-model="form.billingCycle"
        :class="['opc-card-radio']"
        :aria-label="`${ctx.title} Billing Cycle`"
        @change="submitForm"
      >
        <zm-radio v-for="plan in currentFilterPlans" :data-testid="`checkout-radio-billing-cycle-${plan.plan_code}`" :key="plan.billing_cycle" :label="plan.billing_cycle"
          :disabled="ctx.billingCycleDisabled(plan)" class="opc-zmone__radio" :class="[!ctx.mxIsSimplifyForProEdit ? 'compact' : '']">
          <p class="opc-zmone__radio-title">
            <span>{{ ctx.getBillingCycleText(plan.billing_cycle) }}</span>
            <CycleCurrentBadge :value="plan" v-if="!ctx.showAnnualTermMonthly || ctx.mxIsSimplifyForProEdit"/>
            <save-count
              v-if="ctx.showSaveCount(plan)"
              :disabled="ctx.billingCycleDisabled(plan)"
              :annual-price="ctx.annuallyPrice"
              :monthly-price="ctx.monthlyPrice"
              :class="[ctx.mxIsSimplifyForProEdit ? 'light' : '']"
            />
          </p>
          <subscription-cycle-sub-title :billingCycle="plan.billing_cycle" :currentCategory="currentCategory" v-if="ctx.showAnnualTermMonthly"/>
          <p class="opc-product-price-container" tabindex="-1" :aria-label="ctx.ariaPriceOfMonthForLabel(plan)" v-if="!ctx.mxIsSimplifyForProEdit">
            <span class="opc-product-price-container__price" aria-hidden="true">
              {{ ctx.getFormattedPriceOfMonth(plan, { forceDiff: true }) }}
            </span>
          </p>
          <p class="opc-zmone__radio-price-unit opc-price-bill-text">
            <span v-html="ctx.getBilledText(plan)"></span><priceVATTip /><span v-if="ctx.showAnnualTermMonthly">*</span>
          </p>
          <CycleCurrentBadge :value="plan" v-if="ctx.showAnnualTermMonthly && !ctx.mxIsSimplifyForProEdit" standalone />
        </zm-radio>
      </zm-radio-group>
    </zm-form-item>

    <preview-payment />
  </zm-form>
</template>
<script>
import { planTypes, BILLING_CYCLE } from '@/common/constants';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import UserChanges from '@/components/users-change.vue';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import PriceVATTip from '@/components/price-vat-tip.vue';
import { mapGetters } from 'vuex';
import BigJs from 'big.js';
import { throttle } from 'lodash';
import { mapActions, mapMutations } from 'vuex';
import { isEmptyVal } from '@/utils';
import SubscriptionSwitch from './subscription-switch.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment';

export default {
  name: 'MultipleMeetingSingleEditForm',
  components: {
    InputNumberWithPopover,
    UserChanges,
    BoughtLicense,
    PriceVATTip,
    SubscriptionSwitch,
    PreviewPayment,
  },
  props: {
    ctx: {
      type: Object,
      required: true,
    }
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'oldPlanCode', 'boughtPlans', 'planMapFlat', 'currentEditPlan']),
    currentCategory() {
      return this.currentEditPlan?.category;
    },
    allMeetingPlans() {
      return this.planMapFlat.filter(plan => plan.type === planTypes.ZOOM_MEETING && plan.category === this.currentCategory);
    },
    selectedMeetingPlans() {
      return this.getSelectedPlans.filter(plan => plan.type === planTypes.ZOOM_MEETING && plan.category === this.currentCategory);
    },
    currentSelectedMeetingPlan() {
      return this.selectedMeetingPlans[0];
    },
    boughtMeetingPlans() {
      return this.boughtPlans.filter(plan => plan.type === planTypes.ZOOM_MEETING && plan.category === this.currentCategory);
    },
    currentFilterPlans() {
      return this.allMeetingPlans.filter(plan =>
        !this.boughtMeetingPlans.some(boughtPlan => boughtPlan.plan_code === plan.plan_code) ||
        plan.plan_code === this.oldPlanCode
      );
    }
  },
  data() {
    return {
      planTypes,
      isInitUpdateForm: false,
      lastSelectedPlanCode: '',
      form: {
        selectedNumber: 0,
        billingCycle: BILLING_CYCLE.MONTHLY,
      }
    }
  },
  methods: {
    ...mapActions(['switchSelectPlan']),
    ...mapMutations(['updateSelectPlan', 'setOldPlanCode']),
    getMonthlyPrice(amount, cycleValue, options = {}) {
      return this.$money(new BigJs(amount).div(cycleValue).toNumber(), options)
    },
    getCurrentBilledText() {
      const boughtBillingCycle = this.currentEditPlan?.billing_cycle;
      if (boughtBillingCycle === BILLING_CYCLE.MONTHLY) {
        return this.$t('billing.simplify.current_billed_monthly');
      } else if (boughtBillingCycle === BILLING_CYCLE.ANNUAL) {
        return this.$t('billing.simplify.current_billed_annually');
      } else {
        return ''
      }
    },
    updateForm(isInitUpdateForm = false) {
      // pre check from params
      const shoppingCartPlan = this.currentSelectedMeetingPlan;
      if (shoppingCartPlan) {
        this.form.billingCycle = shoppingCartPlan.billing_cycle;
        this.form.selectedNumber = shoppingCartPlan.cartInfo.selected_number;
        this.lastSelectedPlanCode = shoppingCartPlan.plan_code;
        this.ctx.mxCheckCollapseSimplifyView(this.form);
      }

      this.isInitUpdateForm = isInitUpdateForm;
    },
    submitForm: throttle(function (v) {
      this.$nextTick(() => {
        if (!this.currentSelectedMeetingPlan) {
          return;
        }

        if (isEmptyVal(v)) {
          this.form.selectedNumber = Math.max(this.currentSelectedMeetingPlan?.cartInfo?.selected_number ?? 0, this.ctx.minHost);
        }

        if (this.currentSelectedMeetingPlan.plan_code === this.lastSelectedPlanCode) {
          this.updateSelectPlan({
            ...this.currentSelectedMeetingPlan,
            cartInfo: {
              selected_number: this.form.selectedNumber,
            },
          });
        } else {
          this.switchSelectPlan([
            this.lastSelectedPlanCode,
            this.currentSelectedMeetingPlan.plan_code,
            {
              selected_number: this.form.selectedNumber,
            },
          ]);
        }
        this.updateForm();
      });
    }, 200),
    handleSwitchPlan({ planCode, billingCycle, licenses }) {
      this.form.billingCycle = billingCycle;
      this.form.selectedNumber = licenses;
      this.lastSelectedPlanCode = planCode;
    }
  },
  created() {
    this.updateForm(true);
  },
};
</script>
<style lang="scss">
.opc-zmone__form-label {
  margin-bottom: 10px;
}
.opc-simplify .opc-zmone__main .opc-config-form {
  .opc-zmone-single-edit__form-item.zbo-form-item {
    margin-bottom: 16px;
  }
}
.opc-config-form {
  .opc-zmone-single-edit__form-item.zbo-form-item {
    .zbo-form-item__widgets {
      .opc-zmone-single-edit__form-item-content-row {
        display: flex;
        &>div {
          flex: 1;
        }
      }
    }
    .zbo-form-item__widgets .opc-zmone__quantity-selector-container {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .price-row {
      color: var(--zoom-color-text-neutral, #686F79);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%; /* 16.8px */
    }
  }
}
</style>
